/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { adminApi } from '../lib/api';
import { useAuth } from './AuthContext';

const themes = {
  light: { name: 'Clair', mode: 'light', sidebarFrom: '#1e293b', sidebarTo: '#0f172a', swatch: '#f8fafc' },
  blue: { name: 'Bleu', mode: 'blue', sidebarFrom: '#0b3b73', sidebarTo: '#082f5b', swatch: '#2563eb' },
  dark: { name: 'Dark', mode: 'dark', sidebarFrom: '#111827', sidebarTo: '#020617', swatch: '#111827' },
};

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [currentTheme, setCurrentTheme] = useState('light');

  useEffect(() => {
    const preferred = user?.theme && themes[user.theme] ? user.theme : localStorage.getItem('projethub-theme');
    setCurrentTheme(themes[preferred] ? preferred : 'light');
  }, [user]);

  useEffect(() => {
    const theme = themes[currentTheme] || themes.light;
    const root = document.documentElement;
    root.dataset.theme = theme.mode;
    root.style.setProperty('--sidebar-from', theme.sidebarFrom);
    root.style.setProperty('--sidebar-to', theme.sidebarTo);
  }, [currentTheme]);

  const changeTheme = useCallback(async (themeKey) => {
    if (!themes[themeKey]) return;
    setCurrentTheme(themeKey);
    localStorage.setItem('projethub-theme', themeKey);
    if (user?.id) {
      try { await adminApi.updateUserTheme(user.id, themeKey); } catch {}
    }
  }, [user]);

  return (
    <ThemeContext.Provider value={{ currentTheme, changeTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
